import React, { useContext, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import filesize from 'filesize';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import LoadingButton from '@mui/lab/LoadingButton';
import { MdDeleteForever, MdFileDownload, MdVisibility } from 'react-icons/md';
import { toast } from 'react-toastify';
import Toast from '../../../../../../components/Toast';
import { options } from '../../../../../../helpers/toastOptions';
import { useAuth } from '../../../../../../hooks/Auth';
import { useModal } from '../../../../../../hooks/Modal';
import { usePickup } from '../../../../../../hooks/Pickup';
import { useStorage } from '../../../../../../hooks/Storage';
import {
  ModalTitle,
  ModalText,
  LabelFileInput,
  FileInput,
  IconButton
} from './styled';
import { SearchMyCollectionsContext } from '../..';

export const ModalUploadDocuments = () => {
  const [otherFiles, setOtherFiles] = useState([]);
  const [removeFile, setRemoveFile] = useState('');
  const [confirmRemoveFile, setConfirmRemoveFile] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [documentTypeErrorList, setDocumentTypeErrorList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inputFileRef = useRef(null);

  const { token } = useAuth();
  const { closeModal, Modal } = useModal();
  const { uploadTaxDocument } = usePickup();
  const { uploadFile } = useStorage();

  const {
    isLoadingPickupDetails,
    protocolToModal,
    setProtocolToModal,
    pickupIdToModal,
    branchParameterizationToModal,
    setPickupIdToModal,
    uploadConfigToModal,
    setUploadConfigToModal,
    limitToUploadInvoice,
    isPickupWithDeclaration,
    refreshSearch
  } = useContext(SearchMyCollectionsContext);

  const maxXS = useMediaQuery('(max-width:480px)');
  const maxSM = useMediaQuery('(max-width:620px)');

  const handleAddInvoiceFiles = (event) => {
    const { files } = event.target;

    const newOtherFiles = [...otherFiles];

    Array.from(files).forEach((file) => {
      newOtherFiles.push({
        id: uuid(),
        file,
        name: file.name,
        size: file.size,
        readableSize: filesize(file.size),
        documentType: ''
      });
    });

    setOtherFiles(newOtherFiles);

    inputFileRef.current.value = '';
  };

  const handleSetDocumentType = (event, id) => {
    const { value: documentType } = event.target;

    const indexFile = otherFiles.findIndex((file) => file.id === id);

    const newOtherFiles = [...otherFiles];
    const newDocumentTypeErrorList = [...documentTypeErrorList];

    newOtherFiles[indexFile].documentType = documentType;

    const index = newDocumentTypeErrorList.findIndex((value) => value === id);

    index >= 0 && newDocumentTypeErrorList.splice(index, 1);

    setOtherFiles(newOtherFiles);
    setDocumentTypeErrorList(newDocumentTypeErrorList);
  };

  const handleRemoveInvoice = () => {
    const newOtherFiles = [
      ...otherFiles.filter((file) => file.id !== removeFile)
    ];

    setOtherFiles(newOtherFiles);

    setRemoveFile('');
  };

  const handleOpenInvoiceFile = (invoiceFile) => {
    const file = new Blob([invoiceFile], { type: invoiceFile.type });
    const fileURL = URL.createObjectURL(file);
    const pdfWindow = window.open();
    pdfWindow.location.href = fileURL;
  };

  const handleDownloadInvoiceFile = (invoiceFile) => {
    const file = new Blob([invoiceFile], { type: invoiceFile.type });
    const fileURL = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = fileURL;
    link.setAttribute('download', invoiceFile.name);

    document.body.appendChild(link);

    link.click();

    link.parentNode.removeChild(link);
  };

  const handleCloseModal = () => {
    setProtocolToModal('');
    setPickupIdToModal('');
    setUploadConfigToModal({});
    closeModal();
  };

  const handleBack = () => {
    otherFiles.length > 0 ? setConfirmClose(true) : handleCloseModal();
  };

  const handleUpload = async () => {
    if (otherFiles.length === 0) {
      toast(
        <Toast
          title="Atenção"
          content="Não foi anexado nenhum documento."
          type="error"
        />,
        options
      );

      return;
    }

    let hasMissingDocumentType = false;
    const newDocumentTypeErrorList = [...documentTypeErrorList];

    otherFiles?.forEach((file) => {
      if (file.documentType === '') {
        hasMissingDocumentType = true;

        !newDocumentTypeErrorList.includes(file.id) &&
          newDocumentTypeErrorList.push(file.id);
      } else {
        const index = newDocumentTypeErrorList.findIndex(
          (id) => id === file.id
        );

        index >= 0 && newDocumentTypeErrorList.splice(index, 1);
      }
    });

    setDocumentTypeErrorList(newDocumentTypeErrorList);

    if (hasMissingDocumentType) {
      toast(
        <Toast
          title="Atenção!"
          content="Campo “Tipo de Documento” não preenchido."
          type="error"
        />,
        options
      );

      return;
    }

    try {
      setIsSubmitting(true);

      let hasFailedUploadDocument = false;
      let firstDeclaration = true;

      // eslint-disable-next-line no-restricted-syntax
      for await (const otherFile of otherFiles) {
        const data = {
          pickupId: pickupIdToModal,
          taxDocumentTypeId: otherFile.documentType,
          parameterizationId: branchParameterizationToModal,
          storageUrl: await uploadFile({
            file: otherFile.file,
            token
          })
        };

        if (otherFile.documentType === '3' && firstDeclaration) {
          data.taxQuantity = otherFiles.filter(
            (value) => value.documentType === '3'
          ).length;

          firstDeclaration = false;
        }

        const uploadTaxDocumentResponse = await uploadTaxDocument({
          data,
          token
        });

        if (uploadTaxDocumentResponse.status !== 201) {
          hasFailedUploadDocument = true;
        }
      }

      if (hasFailedUploadDocument) {
        toast(
          <Toast
            title="Atenção!"
            content="Falha ao anexar um ou mais documentos."
            type="error"
          />,
          options
        );
      } else {
        toast(
          <Toast
            title="Sucesso!"
            content="Documentos anexados com sucesso."
            type="success"
          />,
          options
        );

        refreshSearch();
        handleCloseModal();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
      setIsSubmitting(false);

      toast(
        <Toast
          title="Atenção!"
          content="Falha ao anexar documentos."
          type="error"
        />,
        options
      );
    }
  };

  return (
    <Modal preventClose>
      {!confirmClose && !confirmRemoveFile && (
        <Stack spacing={2}>
          <ModalTitle>Upload de Outros Documentos</ModalTitle>

          <Stack
            spacing={2}
            style={{
              maxHeight: '86vh',
              overflow: 'auto'
            }}
          >
            <ModalText>Protocolo: {protocolToModal}</ModalText>

            <Box style={{ display: 'flex' }}>
              <LabelFileInput
                htmlFor="other-files"
                style={{
                  width: '100%',
                  color:
                    isLoadingPickupDetails || isSubmitting
                      ? '#bdbdbd'
                      : '#e38500',
                  backgroundColor:
                    isLoadingPickupDetails || isSubmitting
                      ? '#ffffff'
                      : 'ffffff',
                  border:
                    isLoadingPickupDetails || isSubmitting
                      ? '1px solid #bdbdbd'
                      : '1px solid rgba(227, 133, 0, 0.5)'
                }}
                disabled={isLoadingPickupDetails || isSubmitting}
              >
                ANEXAR OUTROS DOCUMENTOS
              </LabelFileInput>
              <FileInput
                ref={inputFileRef}
                id="other-files"
                type="file"
                multiple
                onChange={handleAddInvoiceFiles}
                disabled={isLoadingPickupDetails || isSubmitting}
              />
            </Box>

            {otherFiles.length > 0 && (
              <Divider orientation="horizontal" flexItem />
            )}

            <Stack
              gap={2}
              divider={<Divider orientation="horizontal" flexItem />}
            >
              {otherFiles.map(
                ({ id, file, name, readableSize, documentType }) => {
                  if (file) {
                    return (
                      <Stack key={id} gap={2} style={{ width: '100%' }}>
                        <Stack
                          direction={maxXS ? 'column' : 'row'}
                          gap={maxXS && 1}
                          style={{
                            width: maxSM ? '100%' : 488,
                            display: 'flex',
                            flexDirection: maxXS ? 'column' : 'row',
                            alignItems: maxXS ? 'flex-start' : 'center',
                            justifyContent: maxXS ? 'center' : 'space-between'
                          }}
                        >
                          <Box
                            style={{
                              maxWidth: maxXS ? '100%' : 160,
                              display: 'flex',
                              flexDirection: 'column',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <strong
                              style={{
                                fontSize: 14,
                                width: maxXS ? '60vw' : 160,
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {name}
                            </strong>
                            <span
                              style={{
                                fontSize: 12,
                                color: '#999',
                                marginTop: 4
                              }}
                            >
                              {readableSize}
                            </span>
                          </Box>

                          <Stack
                            direction="row"
                            gap={1}
                            style={{
                              width: 360,
                              alignSelf: maxXS && 'center'
                            }}
                          >
                            <IconButton
                              type="button"
                              disabled={isSubmitting}
                              onClick={() => handleOpenInvoiceFile(file)}
                            >
                              <MdVisibility size={20} />
                            </IconButton>

                            <IconButton
                              type="button"
                              disabled={isSubmitting}
                              onClick={() => handleDownloadInvoiceFile(file)}
                            >
                              <MdFileDownload size={20} />
                            </IconButton>

                            <IconButton
                              type="button"
                              disabled={isSubmitting}
                              onClick={() => {
                                setRemoveFile(id);
                                setConfirmRemoveFile(true);
                              }}
                            >
                              <MdDeleteForever size={20} />
                            </IconButton>

                            <FormControl>
                              <InputLabel size="small">
                                Tipo de Documento: *
                              </InputLabel>
                              <Select
                                label="Tipo de Documento: *"
                                size="small"
                                disabled={isSubmitting}
                                value={documentType}
                                onChange={(event) => {
                                  handleSetDocumentType(event, id);
                                }}
                                style={{ width: 204 }}
                                error={documentTypeErrorList.includes(id)}
                              >
                                <MenuItem value="" disabled />
                                <MenuItem
                                  value="1"
                                  disabled={
                                    !uploadConfigToModal?.enableAuthorizationLetterUpload
                                  }
                                >
                                  Carta de Autorização
                                </MenuItem>
                                <MenuItem
                                  value="2"
                                  disabled={
                                    !uploadConfigToModal?.enableFailureLetterUpload
                                  }
                                >
                                  Carta de Avaria
                                </MenuItem>
                                <MenuItem
                                  value="3"
                                  disabled={
                                    !uploadConfigToModal?.enableDeclarationUpload ||
                                    !isPickupWithDeclaration ||
                                    otherFiles?.filter(
                                      (value) => value.documentType === '3'
                                    )?.length >= limitToUploadInvoice
                                  }
                                >
                                  Declaração
                                </MenuItem>
                                <MenuItem
                                  value="4"
                                  disabled={
                                    !uploadConfigToModal?.enableMsdsUpload
                                  }
                                >
                                  FISPQ
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Stack>
                        </Stack>
                      </Stack>
                    );
                  }
                  return null;
                }
              )}
              {otherFiles.length === 0 && (
                <Stack style={{ width: !maxXS && 464, alignItems: 'center' }}>
                  <span>Sem arquivos selecionados</span>
                </Stack>
              )}
            </Stack>

            <Box
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 32
              }}
            >
              <Button
                type="button"
                variant="contained"
                color="secondary"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <LoadingButton
                type="button"
                variant="contained"
                onClick={handleUpload}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Salvar
              </LoadingButton>
            </Box>
          </Stack>
        </Stack>
      )}

      {confirmRemoveFile && (
        <Stack spacing={2}>
          <ModalTitle>Atenção</ModalTitle>

          <ModalText>
            O arquivo anexado será excluído da solicitação. <br /> Deseja
            Continuar?
          </ModalText>

          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 32
            }}
          >
            <Button
              type="button"
              variant="contained"
              color="attention"
              onClick={() => {
                setRemoveFile('');
                setConfirmRemoveFile(false);
              }}
            >
              Não
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={() => {
                handleRemoveInvoice();
                setConfirmRemoveFile(false);
              }}
            >
              Sim
            </Button>
          </Box>
        </Stack>
      )}

      {confirmClose && (
        <Stack spacing={2}>
          <ModalTitle>Atenção</ModalTitle>

          <ModalText>Deseja cancelar o upload de outros documentos?</ModalText>

          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 32
            }}
          >
            <Button
              type="button"
              variant="contained"
              color="attention"
              onClick={() => setConfirmClose(false)}
            >
              Não
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={() => handleCloseModal()}
            >
              Sim
            </Button>
          </Box>
        </Stack>
      )}
    </Modal>
  );
};
